/**
 * Ctrl+K (⌘K on a Mac): jump anywhere. Type a page ("arrests"), an action ("new incident"),
 * or a record number / name and press Enter to search for it.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '../shared/icons.tsx';
import { useRouter } from '../shared/router.tsx';

export interface JumpItem {
  to: string;
  label: string;
  icon: string;
  group: string;
  words?: string;
}

export function QuickJump({ items }: { items: JumpItem[] }) {
  const { navigate } = useRouter();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [hi, setHi] = useState(0);
  const box = useRef<HTMLInputElement>(null);
  // Opening always starts with an empty box (cleared before it shows, so fast typing isn't mixed with the last search).
  const show = () => (setQ(''), setHi(0), setOpen(true));
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => {
          if (!o) (setQ(''), setHi(0));
          return !o;
        });
      } else if (e.key === 'Escape') setOpen(false);
    };
    const onOpen = () => show();
    window.addEventListener('keydown', onKey);
    window.addEventListener('civ-quickjump', onOpen);
    return () => (window.removeEventListener('keydown', onKey), window.removeEventListener('civ-quickjump', onOpen));
  }, []);
  useEffect(() => {
    if (open) box.current?.focus();
  }, [open]);
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    const list = t ? items.filter((i) => `${i.label} ${i.group} ${i.words ?? ''}`.toLowerCase().includes(t)) : items;
    const out = list.slice(0, 9);
    if (t.length >= 2) out.push({ to: `/search?q=${encodeURIComponent(q.trim())}`, label: `Search everything for “${q.trim()}”`, icon: 'search', group: 'Search' });
    return out;
  }, [q, items]);
  if (!open) return null;
  const go = (i: JumpItem | undefined) => {
    if (!i) return;
    setOpen(false);
    navigate(i.to);
  };
  return (
    <div className="qj-back" onMouseDown={() => setOpen(false)}>
      <div className="qj" role="dialog" aria-modal="true" aria-label="Jump to" onMouseDown={(e) => e.stopPropagation()}>
        <div className="qj-in">
          <Icon name="search" size={18} />
          <input
            ref={box}
            autoFocus
            value={q}
            onChange={(e) => (setQ(e.target.value), setHi(0))}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') (e.preventDefault(), setHi((h) => Math.min(h + 1, shown.length - 1)));
              else if (e.key === 'ArrowUp') (e.preventDefault(), setHi((h) => Math.max(h - 1, 0)));
              else if (e.key === 'Enter') (e.preventDefault(), go(shown[hi]));
            }}
            placeholder="Go to a page, start something, or search a case #, name or tag"
            aria-label="Jump to"
            aria-controls="qj-list"
            aria-activedescendant={shown[hi] ? `qj-${hi}` : undefined}
          />
          <kbd>Esc</kbd>
        </div>
        <ul className="qj-list" id="qj-list" role="listbox">
          {shown.map((i, k) => (
            <li key={i.to + i.label} id={`qj-${k}`} role="option" aria-selected={k === hi} className={k === hi ? 'on' : ''} onMouseEnter={() => setHi(k)} onClick={() => go(i)}>
              <Icon name={i.icon} size={17} />
              <span className="qj-l">{i.label}</span>
              <span className="qj-g">{i.group}</span>
            </li>
          ))}
          {shown.length === 0 && <li className="qj-none">Nothing matches. Type at least 2 letters to search records.</li>}
        </ul>
        <div className="qj-foot">
          <span>
            <kbd>↑</kbd> <kbd>↓</kbd> to choose
          </span>
          <span>
            <kbd>Enter</kbd> to open
          </span>
          <span>
            <kbd>Ctrl</kbd> <kbd>K</kbd> anytime
          </span>
        </div>
      </div>
    </div>
  );
}
